import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import FoundingBackersWall from "../components/FoundingBackersWall";
import InteractivePipDemo from "../components/InteractivePipDemo";
import { LOGO_ANIMATED } from "../constants";

function useInView(ref, rootMargin = "0px") {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { rootMargin, threshold: 0.15 });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, rootMargin]);
  return visible;
}

function AnimatedCard({ children, delay, className = "", testId }) {
  const ref = useRef(null);
  const visible = useInView(ref);
  return (
    <div ref={ref} className={`anim-card-enter ${visible ? "visible" : ""} ${className}`} style={{ transitionDelay: `${delay}ms` }} data-testid={testId}>
      {children}
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  
  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Parallax rates: logo moves slowest, text faster, buttons fastest
  const logoOffset = scrollY * 0.15;
  const textOffset = scrollY * 0.3;
  const ctaOffset = scrollY * 0.45;
  const heroOpacity = Math.max(0, 1 - scrollY / 700);
  
  return (
    <Layout>
      <div className="relative min-h-[calc(100vh-4rem)] flex items-start justify-center overflow-hidden pt-4">
        <div className="absolute inset-0 hero-glow" />
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto" style={{ opacity: heroOpacity }}>
          <div className="mb-0 anim-entrance anim-delay-1" style={{ transform: `translateY(${logoOffset}px)`, willChange: 'transform' }}>
            <div className="animate-float">
              <img src={LOGO_ANIMATED} alt="Top Screen" className="w-full max-w-[693px] h-auto mx-auto -my-24" />
            </div>
          </div>
          <p className="text-white text-lg mb-6 mt-[120px] max-w-3xl mx-auto neon-white-text anim-entrance anim-delay-2" style={{ transform: `translateY(${textOffset}px)`, willChange: 'transform' }}>
            <span className="block">The ultimate picture-in-picture experience. Keep your content visible,</span>
            <span className="block">always on top, and fully customizable across all your devices.</span>
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center anim-entrance anim-delay-3" style={{ transform: `translateY(${ctaOffset}px)`, willChange: 'transform' }}>
            <button onClick={() => navigate('/register')} className="btn-primary text-lg px-8 py-4" data-testid="hero-get-started">
              Get Started Free
            </button>
            <button onClick={() => navigate('/introducing')} className="btn-secondary text-lg px-8 py-4" data-testid="hero-learn-more">
              Learn More
            </button>
          </div>
        </div>
      </div>
      
      {/* Live Demo */}
      <section id="try-it-now" className="py-24 px-4" data-testid="demo-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-5xl md:text-7xl font-semibold text-white text-center mb-4 anim-entrance anim-delay-1">Try It Now</h2>
          <p className="text-center mb-12 max-w-2xl mx-auto anim-entrance anim-delay-2">
            <span className="neon-text-white block text-lg md:text-xl">Drag the title bar to move it. Grab the corner to resize.</span>
            <span className="neon-text-white block text-lg md:text-xl">Pause, play, or pop it out — just like the real Top Screen&#8482;</span>
          </p>
          <InteractivePipDemo />
        </div>
      </section>
      
      {/* Features */}
      <section id="features" className="py-24 px-4" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading text-5xl md:text-7xl font-semibold text-white text-center mb-4 anim-entrance anim-delay-1">Everything You Need</h2>
          <p className="text-center text-lg md:text-xl text-[#E8E4DC] mb-16 max-w-3xl mx-auto anim-entrance anim-delay-2">Packed with powerful features to give you complete control<br/>over your picture-in-picture experience.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[
              { title: "PiP Any Tab", desc: "Capture your current tab and keep it visible while working in other applications.", icon: "pip-tab" },
              { title: "PiP Any Website", desc: "Enter any URL and open it in a floating window. Perfect for dashboards and live feeds.", icon: "pip-url" },
              { title: "PiP Videos", desc: "Detect and pip any video on the page. Works with YouTube, Twitch, Netflix, and more.", icon: "pip-video" },
              { title: "Multi-Instance", desc: "Open multiple PiP windows at once. Monitor several sources simultaneously.", icon: "multi" },
              { title: "Opacity Control", desc: "Adjust transparency from 20% to 100%. See through your PiP window to the content below.", icon: "opacity" },
              { title: "Frame Styles", desc: "Choose from Classic, RGB animated, Neon glow, or Shadow frame styles.", icon: "frame" },
              { title: "Smart Positioning", desc: "Snap to corners or drag freely. Your window stays exactly where you want it.", icon: "position" },
              { title: "Theatre Mode", desc: "Dim the background to focus on your PiP content. Perfect for presentations.", icon: "theatre" },
              { title: "Favorites", desc: "Save up to 20 frequently used URLs for instant PiP access.", icon: "favorites" },
              { title: "Scheduling", desc: "Schedule PiP windows to open and close automatically.", icon: "schedule" },
              { title: "Audio Control", desc: "Mute individual windows or all at once. Full audio management.", icon: "audio" },
              { title: "Size Presets", desc: "Choose from Small, Medium, Large, or Extra Large presets.", icon: "size" },
            ].map((feature, i) => (
              <AnimatedCard key={i} delay={i * 80} className="card-glass p-6" testId={`feature-card-${i}`}>
                <div className="w-12 h-12 rounded-xl bg-[#A3E635]/10 flex items-center justify-center mb-4">
                  <FeatureIcon name={feature.icon} />
                </div>
                <h3 className="font-heading text-xl font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-[#E8E4DC]">{feature.desc}</p>
              </AnimatedCard>
            ))}
          </div>
        </div>
      </section>

      {/* Support 713 C&D — Top Screen™ description + urgent CTA */}
      <section id="support" className="py-24 px-4" data-testid="support-section">
        <div className="max-w-4xl mx-auto">
          <div className="card-glass p-10 sm:p-14 border border-[#A3E635]/30">
            <p className="font-heading text-2xl sm:text-3xl text-white leading-snug mb-6" data-testid="support-tagline">
              Top Screen™ is the world's most customizable always-on-top picture-in-picture studio — pin any tab, video, livestream, or website on top of every app you use, exactly the way you want it.
            </p>
            <p className="text-[#E8E4DC] text-base sm:text-lg leading-relaxed mb-8" data-testid="support-paragraph">
              Built for creators, traders, students, remote workers, and anyone who refuses to lose sight of what matters, Top Screen™ delivers true cross-platform PiP across Chrome, Edge, Windows, Mac, iOS, and Android — with multi-window stacking, theatre mode, per-instance audio, custom borders, scheduled launches, and a built-in favorites manager. Every feature you've seen here, every device we still need to ship, every team member we need to hire, and every future <span className="text-[#A3E635] font-semibold">713 C&amp;D</span> innovation depends on backers like you who believe a small Houston team can outbuild Big Tech.
              <br /><br />
              <span className="text-white font-bold">If Top Screen™ has earned even a flicker of your imagination, back us today — your $39 lifetime license, or any contribution beyond it, is the rocket fuel that turns this prototype into a movement and keeps independent innovation alive.</span>
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button onClick={() => navigate('/pricing')} className="btn-primary text-lg px-8 py-4" data-testid="support-cta-lifetime">
                Get Lifetime — $39
              </button>
              <button onClick={() => navigate('/contact')} className="btn-secondary text-lg px-8 py-4" data-testid="support-cta-contact">
                Become a Backer
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Founding Backers Wall */}
      <FoundingBackersWall />
    </Layout>
  );
}

function FeatureIcon({ name }) {
  const cls = "w-6 h-6 text-[#A3E635]";
  switch(name) {
    case 'pip-tab': return <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><rect x="12" y="10" width="9" height="6" rx="1" fill="currentColor" opacity="0.3"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>;
    case 'pip-url': return <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>;
    case 'pip-video': return <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="5 3 19 12 5 21 5 3"/></svg>;
    case 'multi': return <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="14" height="10" rx="1.5"/><rect x="8" y="11" width="14" height="10" rx="1.5"/></svg>;
    case 'opacity': return <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 2a10 10 0 010 20" fill="currentColor" opacity="0.3"/></svg>;
    case 'frame': return <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><rect x="7" y="7" width="10" height="10" rx="1"/></svg>;
    case 'position': return <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 9l-3 3 3 3"/><path d="M9 5l3-3 3 3"/><path d="M15 19l3-3-3-3" transform="rotate(180 15 16.5)"/><path d="M19 9l-3 3 3 3"/><path d="M15 19l-3 3-3-3"/><rect x="8" y="8" width="8" height="8" rx="1" fill="currentColor" opacity="0.2"/></svg>;
    case 'theatre': return <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><rect x="6" y="7" width="12" height="8" rx="1" fill="currentColor" opacity="0.3"/><line x1="2" y1="4" x2="6" y2="7"/><line x1="22" y1="4" x2="18" y2="7"/><line x1="2" y1="20" x2="6" y2="15"/><line x1="22" y1="20" x2="18" y2="15"/></svg>;
    case 'favorites': return <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z"/></svg>;
    case 'schedule': return <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
    case 'audio': return <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07"/></svg>;
    case 'size': return <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>;
    default: return null;
  }
}
